import type { PolygonOpenContour } from '../polygon-open-contour';
import type { PointArray } from '../types/contour-definition-array-type';

import { getClosestPointIdx } from './getClosestPointIdx';
import { interpolateGridPoints2D } from './interpolatePolygonToGrid';
import { removeLoops } from './removeLoops';
import { snapPolyLineToSubPixelGrid } from './snapPolyLineToSubPixelGrid';

export function createLaxMyocardialPolygon(
  endoContour: PolygonOpenContour,
  epiContour: PolygonOpenContour,
): PointArray {
  const endo = endoContour.getPolygon();
  const epi = epiContour.getPolygon();
  if (endo.length < 2 || epi.length < 2) {
    return [];
  }

  const epiEnds: PointArray = [epi[0], epi[epi.length - 1]];
  const closestEndIdx = getClosestPointIdx(endo[endo.length - 1], epiEnds);
  const epiOrdered = closestEndIdx === 0 ? [...epi] : [...epi].reverse();

  // Bridge 1: endo last → epi first
  let bridge1: PointArray = [endo[endo.length - 1], epiOrdered[0]];
  bridge1 = interpolateGridPoints2D(bridge1, 1);
  bridge1 = snapPolyLineToSubPixelGrid(bridge1, false);

  // Bridge 2: epi last → endo first (closing)
  let bridge2: PointArray = [epiOrdered[epiOrdered.length - 1], endo[0]];
  bridge2 = interpolateGridPoints2D(bridge2, 1);
  bridge2 = snapPolyLineToSubPixelGrid(bridge2, false);

  // Assemble: endo (exact) → bridge1 (skip endpoints to avoid duplicates) → epi (exact) → bridge2 → close
  let fusion: PointArray = [
    ...endo,
    ...bridge1.slice(1, -1),
    ...epiOrdered,
    ...bridge2.slice(1, -1),
  ];
  // remove ALL duplicate points, keep first occurrence
  const seen = new Set<string>();
  fusion = fusion.filter((p) => {
    const key = `${p.x},${p.y}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (
    fusion[0].x !== fusion[fusion.length - 1].x
    || fusion[0].y !== fusion[fusion.length - 1].y
  ) {
    fusion.push({ x: fusion[0].x, y: fusion[0].y });
  }

  fusion = removeLoops(fusion);

  return fusion;
}
